import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { apiClient } from "../../lib/api";
import { Btn } from "../../lib/primitives";
import { Icons, Cascade } from "../../lib/icons";
import { OnboardingShell } from "./OnboardingShell";

/**
 * Step 2: how AuthenSee integrates, in three steps. No animations — just
 * clear copy with a static "diagram" built from existing brand SVGs.
 *
 * The Cascade glyphs aren't load-bearing UI — they're brand garnish around
 * the three numbered cards (server -> hosted page -> server). Operators
 * who skim still get the gist from the eyebrow + body copy.
 */
const STEPS = [
  {
    n: 1,
    title: "Your server creates a session",
    body:
      "Call POST /v1/hosted/sessions with your sk_ key and the user you want to authenticate. We hand back a one-time URL.",
  },
  {
    n: 2,
    title: "The person signs in on our page",
    body:
      "Send them to that URL. AuthenSee handles the passkey, picture spots or motion, recovery and rate limits, all on our domain.",
  },
  {
    n: 3,
    title: "Your server receives a signed JWT",
    body:
      "When they're done we POST a signed JWT to your callback URL. Check the signature, then start your own session.",
  },
];

export function Explainer() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () => apiClient.onboarding.patch({ step: "generate_key" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding"] });
      navigate("/onboarding/generate-key", { replace: true });
    },
  });

  const back = useMutation({
    mutationFn: () => apiClient.onboarding.patch({ step: "survey" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding"] });
      navigate("/onboarding/survey", { replace: true });
    },
  });

  return (
    <OnboardingShell current="explainer">
      <div className="ad-eyebrow" style={{ marginBottom: 6 }}>
        Step 2 of 4 · How it works
      </div>
      <h1 className="ad-h1" style={{ fontSize: 28, marginBottom: 8 }}>
        Three steps. <em>That's it.</em>
      </h1>
      <div className="ad-sub" style={{ marginBottom: 24 }}>
        People sign in on AuthenSee's page, and your server stays in charge of
        sessions. We only tell you "this person signed in, this way, at this
        time".
      </div>

      <div
        className="ad-stack-md"
        style={{ position: "relative", marginBottom: 28 }}
      >
        {STEPS.map((s) => (
          <div
            key={s.n}
            className="ad-card"
            style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
              padding: 18,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--ad-orange-soft)",
                color: "var(--ad-orange-ink)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {s.n}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--ad-fg-1)",
                  marginBottom: 4,
                }}
              >
                {s.title}
              </div>
              <div style={{ fontSize: 13, color: "var(--ad-fg-3)", lineHeight: 1.55 }}>
                {s.body}
              </div>
            </div>
            <div style={{ opacity: 0.18, flexShrink: 0 }}>
              <Cascade size={32} />
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 12,
        }}
      >
        <Btn
          kind="ghost"
          onClick={() => back.mutate()}
          disabled={back.isPending}
          leading={<Icons.ChevronL size={14} />}
        >
          Back
        </Btn>
        <Btn
          kind="primary"
          onClick={() => submit.mutate()}
          disabled={submit.isPending}
          trailing={<Icons.ArrowR size={14} />}
        >
          {submit.isPending ? "Saving…" : "Continue"}
        </Btn>
      </div>
    </OnboardingShell>
  );
}
